import { PageLayout } from "@src/common/PageLayout";
import { ThemedIllustration } from "@src/common/ThemedIllustration";
import { styled } from "@uhg-abyss/web/tools/styled";
import { Router } from "@uhg-abyss/web/ui/Router";
import { ToastProvider } from "@uhg-abyss/web/ui/Toast";
import type { ReactNode } from "react";

export type ResponsiveDevice = "mobile" | "tablet" | "desktop";

interface IllustratedRouteLayoutProps {
  children: ReactNode;
  metaTitle: string;
  device: ResponsiveDevice;
}

const IllustrationSection = styled("div", {
  display: "flex",
  alignItems: "center",
  variants: {
    device: {
      tablet: {
        marginTop: "100px",
      },
      desktop: {
        marginTop: "0px",
      },
    },
  },
});

const BackgroundContainer = styled("div", {
  width: "100%",
  height: "100%",
  position: "relative",
  zIndex: 2,
});

const MainContainer = styled("div", {
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  width: "100%",
});

const ZeroPaddingPageLayout = styled(PageLayout, {
  padding: "0px",
});

const ContentSection = styled("div", {
  flex: 3,
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  justifyContent: "center",
  paddingLeft: "124px",
  paddingRight: "10px",
  margin: "50px 0px",
  width: "100%",
  variants: {
    device: {
      mobile: {
        padding: "32px 20px",
      },
      tablet: {
        paddingLeft: "70px",
      },
    },
  },
});

export const IllustratedRouteLayout = ({
  children,
  metaTitle,
  device,
}: IllustratedRouteLayoutProps) => {
  const isMobile = device === "mobile";
  const isTablet = device === "tablet";
  const LightHouseIllustrationStyle = {
    transform: "rotateY(180deg)",
    width: isTablet ? "547px" : "518px",
    height: "auto",
  };

  return (
    <>
      <ToastProvider />
      <ZeroPaddingPageLayout backgroundColor="$app.color.top.background">
        <Router.MetaTags title={metaTitle} />
        <BackgroundContainer>
          <MainContainer>
            <ContentSection device={device}>{children}</ContentSection>
            {!isMobile && (
              <IllustrationSection device={device}>
                <ThemedIllustration
                  themeIllustrationName="lighthouse"
                  altText="Lighthouse illustration"
                  themedImageStyle={LightHouseIllustrationStyle}
                  brandProps={{
                    illustration: "lighthouse",
                    css: {
                      "abyss-illustration-brand-root": {
                        ...LightHouseIllustrationStyle,
                      },
                    },
                  }}
                />
              </IllustrationSection>
            )}
          </MainContainer>
        </BackgroundContainer>
      </ZeroPaddingPageLayout>
    </>
  );
};
